import { RefObject, useEffect, useRef, useState } from 'react'

import { DIGITANE_VIDEO_START_TIMEOUT_MS } from './config'
import { loadDigitaneVideo } from './embedLoader'
import {
  DIGITANE_VIDEO_EVENT_TYPES,
  DigitaneVideoEventHandlers,
  DigitaneVideoInitOptions,
  DigitaneVideoInstance,
} from './types'

export type DigitaneVideoStatus = 'idle' | 'loading' | 'embedded' | 'error'

export interface UseDigitaneVideoOptions {
  /** iframe を入れる要素。マウントされていない（current が null）と初期化せずエラーにする */
  readonly targetRef: RefObject<HTMLElement | null>
  /** init に渡す設定。null なら初期化しない（既存のインスタンスは破棄する） */
  readonly options: DigitaneVideoInitOptions | null
  /** イベントハンドラ。参照が変わっても再初期化しない（常に最新のものを呼ぶ） */
  readonly handlers?: DigitaneVideoEventHandlers
  /** 値が変わると destroy して init し直す（autoplay 付きの再初期化などに使う） */
  readonly generation?: number
}

export interface UseDigitaneVideoResult {
  readonly status: DigitaneVideoStatus
  readonly error: Error | null
}

const toError = (value: unknown): Error =>
  value instanceof Error ? value : new Error(String(value))

/**
 * digitane 動画 Widget（embed.js）を targetRef の要素へ埋め込むフック。
 *
 * - options の中身（JSON.stringify した値）か generation が変わったときだけ
 *   destroy して init し直す。オブジェクトの参照が変わるだけでは再初期化しない
 * - handlers は常に最新のものが呼ばれる（差し替えても再初期化しない）
 * - アンマウント時に destroy する。StrictMode の effect 二重実行でも
 *   「破棄して作り直す」だけで iframe は 1 つになる
 * - init から DIGITANE_VIDEO_START_TIMEOUT_MS たっても、その Widget から最初の
 *   イベントが届かなければ、起動しなかったとして destroy し、error にする
 *   （iframe の HTML や起動用の JS を取得できないと、Widget は何も通知しない）。
 *   最初のイベントが届いた後は監視しない
 */
export const useDigitaneVideo = ({
  targetRef,
  options,
  handlers,
  generation = 0,
}: UseDigitaneVideoOptions): UseDigitaneVideoResult => {
  const [status, setStatus] = useState<DigitaneVideoStatus>('idle')
  const [error, setError] = useState<Error | null>(null)

  // 毎レンダーで最新のハンドラ・設定を保持し、effect からはここを読む。
  // effect の依存に入れると、参照が変わるたびに再初期化されてしまうため
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers
  const optionsRef = useRef(options)
  optionsRef.current = options

  // 設定の同一性は参照ではなくシリアライズした値で判定する
  const optionsKey = JSON.stringify(options)

  useEffect(() => {
    const currentOptions = optionsRef.current
    if (currentOptions === null) {
      setError(null)
      setStatus('idle')
      return
    }

    // cancelled は、クリーンアップ（アンマウント・再初期化）の後か、起動しなかった
    // として見限った後。どちらの場合も、この試行の結果やイベントは反映しない
    let cancelled = false
    let instance: DigitaneVideoInstance | null = null
    // この試行の Widget からイベントが一度でも届いたか（起動の監視を止める）
    let started = false
    let startTimer: ReturnType<typeof setTimeout> | undefined
    setError(null)
    setStatus('loading')

    const fail = (label: string, err: unknown): void => {
      const failure = toError(err)
      console.error(label, failure)
      setError(failure)
      setStatus('error')
    }

    // init に渡す on。全種類のイベントを 1 つのオブジェクトで受け、最新のハンドラへ
    // 振り分ける。試行ごとに作り、最初のイベントで起動の監視を止める
    const on: DigitaneVideoEventHandlers = {}
    for (const type of DIGITANE_VIDEO_EVENT_TYPES) {
      on[type] = (event) => {
        if (cancelled) return
        if (!started) {
          started = true
          clearTimeout(startTimer)
        }
        handlersRef.current?.[type]?.(event)
      }
    }

    loadDigitaneVideo()
      .then((api) => {
        // クリーンアップ後（アンマウント・再初期化）に解決した場合は何もしない
        if (cancelled) return

        const target = targetRef.current
        if (target === null) {
          throw new Error('[ERROR] DigitaneVideo target element is not mounted')
        }
        instance = api.init({ ...currentOptions, target, on })
        setStatus('embedded')
        if (started) return

        startTimer = setTimeout(() => {
          if (cancelled || started) return

          // 起動しなかった試行を見限る。遅れて届くイベントは on で捨てる
          cancelled = true
          instance?.destroy()
          instance = null
          fail(
            '[ERROR] DigitaneVideo widget did not start',
            new Error('[ERROR] DigitaneVideo widget did not start'),
          )
        }, DIGITANE_VIDEO_START_TIMEOUT_MS)
      })
      .catch((err: unknown) => {
        if (cancelled) return

        fail('[ERROR] DigitaneVideo init failed', err)
      })

    return () => {
      cancelled = true
      clearTimeout(startTimer)
      instance?.destroy()
    }
  }, [targetRef, optionsKey, generation])

  return { status, error }
}
